import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAlertContext } from "../../../../../alert/AlertContext";
import { useFormikWithDirtyForm } from "../../../../../forms/useFormikWithDirtyForm";
import {
  CreateClientSpecificProceedingParticipantRequest,
  ProceedingParticipantResponse,
  ProceedingParticipantType,
  UpdateProceedingParticipantRequest,
} from "../../../../api/ClientFileApi";
import { useParticipantsApi } from "../../../../api/ClientFileApiContext";
import { ClientSpecificParticipantFormType } from "../../../../participants/participant-form-fields/ParticipantForm.types";
import {
  participantValidationSchema,
  toNullable,
} from "../../../../participants/participant-form-fields/ParticipantFormFields.utils";
import { ClientFileQueryKey } from "../../../../query/clientFileQueryKeys";

interface UseClientSpecificParticipantParams {
  clientId: string;
  allowedTypes: ProceedingParticipantType[];
  prefilledName?: string;
  editParticipantId?: string;
  onCreated: (participant: ProceedingParticipantResponse) => void;
  onClose: () => void;
}

export function useClientSpecificParticipant({
  clientId,
  allowedTypes,
  prefilledName,
  editParticipantId,
  onCreated,
  onClose,
}: UseClientSpecificParticipantParams) {
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();
  const queryClient = useQueryClient();
  const participantsApi = useParticipantsApi();

  const { data: existing, isLoading } = useQuery({
    queryKey: [ClientFileQueryKey.ProceedingParticipants, editParticipantId],
    queryFn: () => participantsApi.getParticipantById({ clientId, participantId: editParticipantId ?? "" }),
    enabled: !!editParticipantId,
  });

  const createMutation = useMutation({
    mutationFn: (request: CreateClientSpecificProceedingParticipantRequest) =>
      participantsApi.createClientSpecificParticipant({
        clientId,
        createClientSpecificProceedingParticipantRequest: request,
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.ProceedingParticipants] });
      showSuccess(t("proceedingParticipants.create.successfullyCreated"));
      onCreated(created);
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (request: UpdateProceedingParticipantRequest) =>
      participantsApi.updateClientSpecificParticipant({
        clientId,
        participantId: editParticipantId ?? "",
        updateProceedingParticipantRequest: request,
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.ProceedingParticipants] }),
        queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.ClientClaims, clientId] }),
        queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.ClaimById], refetchType: "active" }),
      ]);
      showSuccess(t("proceedingParticipants.edit.successfullyUpdated"));
      onClose();
    },
  });

  const validationSchema = useMemo(() => participantValidationSchema(t), [t]);

  const formik = useFormikWithDirtyForm<ClientSpecificParticipantFormType>({
    initialValues: {
      type: existing?.type ?? (allowedTypes.length === 1 ? (allowedTypes[0] ?? null) : null),
      name: existing?.name ?? prefilledName ?? "",
      street: existing?.street ?? "",
      houseNumber: existing?.houseNumber ?? "",
      country: existing?.country ?? "",
      zipCode: existing?.zipCode ?? "",
      city: existing?.city ?? "",
      note: existing?.note ?? null,
      createAsGlobal: false,
    },
    enableReinitialize: !!editParticipantId,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      const basePayload = {
        name: values.name.trim(),
        street: toNullable(values.street),
        houseNumber: toNullable(values.houseNumber),
        country: toNullable(values.country),
        zipCode: toNullable(values.zipCode),
        city: toNullable(values.city),
        note: toNullable(values.note),
      };
      if (editParticipantId !== undefined) {
        await updateMutation.mutateAsync(basePayload);
      } else {
        if (values.type == null) {
          return;
        }
        await createMutation.mutateAsync({
          ...basePayload,
          type: values.type,
          createAsGlobal: values.createAsGlobal,
        });
      }
    },
  });

  return { formik, isLoading };
}
